import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type { ILocalizedInformation, IProduct, ITheme } from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';
import type { IKompisElementPropertyValue } from '@inter-ikea-kompis/web-component';

import { ActionListItem } from '@inter-ikea-kompis/component-action-list';
import { ActionListControlEnum } from '@inter-ikea-kompis/component-action-list';
import { ActionList } from '@inter-ikea-kompis/component-action-list';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';

import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import ProductInformationSectionModalEnum from '../../enums/ProductInformationSectionModalEnum.js';
import type IProductInformationSectionDexfSettings from '../../types/IProductInformationSectionDexfSettings.js';
import type IProductInformationSectionTranslations from '../../types/IProductInformationSectionTranslations.js';
import MeasurementsCard from '../measurements-card/MeasurementsCard.js';
import ProductDetailsCard from '../product-details-card/ProductDetailsCard.js';
import TechnicalInformationCard from '../technical-information-card/TechnicalInformationCard.js';

/**
 * Product information section.
 *
 * @event modalOpen When product information section action list item has been pressed.
 * @event modalClose When the sheet with content has been closed.
 */

export default class ProductInformationSection extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductInformationSectionTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IProductInformationSectionDexfSettings | null = null;

	// Visible modal
	@property({ type: String })
	public visibleModal: ProductInformationSectionModalEnum | null = null;

	// A product with the type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	private sheetElement: SkapaSheet | null;
	private triggerElement: HTMLButtonElement | null = null;
	private cardElement: ProductDetailsCard | TechnicalInformationCard | MeasurementsCard | null =
		null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<kompis-action-list>
				${this.getProductDetailsActionItem()} ${this.getTechnicalInformationActionItem()}
				${this.getProductMeasurementActionItem()}
			</kompis-action-list>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('kompis-action-list', ActionList);
		this.customElements.define('kompis-action-list-item', ActionListItem);
		this.customElements.define('kompis-technical-information-card', TechnicalInformationCard);
		this.customElements.define('kompis-measurements-card', MeasurementsCard);
		this.customElements.define('kompis-product-details-card', ProductDetailsCard);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (
				this.visibleModal === ProductInformationSectionModalEnum.productDetailsCard ||
				this.visibleModal === ProductInformationSectionModalEnum.technicalInformationCard ||
				this.visibleModal === ProductInformationSectionModalEnum.measurementsCard
			) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}

		if (this.cardElement) {
			this.cardElement.theme = this.theme;
			this.cardElement.localizedInformation = this.localizedInformation;
			this.cardElement.translations = this.translations;
			this.cardElement.product = this.product;
			if ('settings' in this.cardElement) {
				this.cardElement.settings = this.settings;
			}
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): IProductInformationSectionTranslations {
		return this.translations
			? this.translations
			: <IProductInformationSectionTranslations>this.localizedInformation?.translations;
	}

	/**
	 * Returns the product details action list item if content is present.
	 */
	private getProductDetailsActionItem(): RenderResult {
		if (!this.product.content) {
			return null;
		}

		return html`
			<kompis-action-list-item
				id="${ProductInformationSectionModalEnum.productDetailsCard}"
				.theme="${this.theme}"
				.label="${this.getTranslations().productCardProductDetailsTitle}"
				.control="${ActionListControlEnum.chevron}"
				@select="${(event: MouseEvent) =>
					this.dispatchModalOpenEvent(
						event,
						ProductInformationSectionModalEnum.productDetailsCard
					)}"
			>
			</kompis-action-list-item>
		`;
	}

	/**
	 * Returns the technical information action list item if content is present.
	 */
	private getTechnicalInformationActionItem(): RenderResult {
		if (!this.product.content?.complianceTechnical && !this.product.content?.technicalInformation) {
			return null;
		}

		return html`
			<kompis-action-list-item
				id="${ProductInformationSectionModalEnum.technicalInformationCard}"
				.theme="${this.theme}"
				.label="${this.getTranslations().productCardTechnicalInformationTitle}"
				.control="${ActionListControlEnum.chevron}"
				@select="${(event: MouseEvent) =>
					this.dispatchModalOpenEvent(
						event,
						ProductInformationSectionModalEnum.technicalInformationCard
					)}"
			>
			</kompis-action-list-item>
		`;
	}

	/**
	 * Returns the product measurement action list item if content is present.
	 */
	private getProductMeasurementActionItem(): RenderResult {
		const hasMeasurementImages = ProductUtility.getProductImages(
			this.product,
			ImageTypeNameEnum.measurementIllustration
		);

		if (!this.product.content?.measure && hasMeasurementImages.length === 0) {
			return null;
		}

		return html`
			<kompis-action-list-item
				id="${ProductInformationSectionModalEnum.measurementsCard}"
				.theme="${this.theme}"
				.label="${this.getTranslations().productCardMeasurementTitle}"
				.control="${ActionListControlEnum.chevron}"
				@select="${(event: MouseEvent) =>
					this.dispatchModalOpenEvent(event, ProductInformationSectionModalEnum.measurementsCard)}"
			>
			</kompis-action-list-item>
		`;
	}

	private closeSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
			this.cardElement = null;
		}
	}

	/**
	 * Dispatches a modalOpen event.
	 *
	 * @param event ...
	 * @param visibleModal ...
	 */
	private dispatchModalOpenEvent(
		event: MouseEvent | KeyboardEvent,
		visibleModal: ProductInformationSectionModalEnum
	): void {
		if (event.target) {
			this.setTriggerElement(event.target);
		}

		this.dispatchEvent(
			new CustomEvent('modalOpen', {
				detail: {
					visibleModal,
					triggerElement: this.triggerElement
				}
			})
		);
	}

	/**
	 * Opens the sheet.
	 */
	private openSheet(): void {
		if (this.sheetElement) {
			return;
		}

		const sheetElement: SkapaSheet = this.createSheetElement();
		const cardElement = this.getCardElementType().card;

		const onClose = (): void => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null,
							triggerElement: this.triggerElement ?? this.getTriggerElement()
						}
					})
				);
			}
		};

		sheetElement.addEventListener('closerequest', onClose);
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		this.sheetElement = sheetElement;
		this.cardElement = cardElement;

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);

		sheetElement.open = true;
	}

	/**
	 * Get trigger element on close.
	 */
	private getTriggerElement(): ActionListItem | null | undefined {
		const actionListItem: ActionListItem | null = this.shadowRoot.querySelector(
			`#${this.visibleModal}`
		);
		return actionListItem;
	}

	/**
	 * Sets trigger element.
	 *
	 * @param target Target.
	 */
	private setTriggerElement(target: EventTarget): HTMLButtonElement | null {
		if (target && target instanceof Element) {
			if (target.shadowRoot) {
				this.triggerElement = target.shadowRoot.querySelector('button');
			}
		}

		return this.triggerElement;
	}

	private createSheetElement(): SkapaSheet {
		const { card, ariaLabel } = this.getCardElementType();

		const sheetHeaderElement = this.createSheetHeader();
		const sheetElement = this.createSheet(ariaLabel);

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(card);

		return sheetElement;
	}

	private createSheet(ariaLabel: string): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');

		sheetElement.setAttribute('aria-label', ariaLabel);
		sheetElement.size = 'small';
		sheetElement.fullHeight = true;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';

		const headerSpan = document.createElement('span');
		headerSpan.slot = 'closebutton-label';
		headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;

		sheetHeaderElement.append(headerSpan);

		return sheetHeaderElement;
	}

	private createCard(
		element:
			| 'kompis-product-details-card'
			| 'kompis-technical-information-card'
			| 'kompis-measurements-card'
	): ProductDetailsCard | TechnicalInformationCard | MeasurementsCard {
		const card = this.createInternalElement(element);
		card.theme = this.theme;
		card.localizedInformation = this.localizedInformation;
		card.translations = this.translations;
		card.product = this.product;

		// Only for MeasurementsCard.
		if ('measurementSystem' in card) {
			card.measurementSystem = this.measurementSystem;
		}

		if ('settings' in card) {
			card.settings = this.settings;
		}

		return card;
	}

	/**
	 * @returns Returns card element and ariaLabel depending on the open modal type.
	 */
	private getCardElementType(): {
		card: ProductDetailsCard | TechnicalInformationCard | MeasurementsCard;
		ariaLabel: string;
	} {
		const {
			productCardTechnicalInformationTitle,
			productCardProductDetailsTitle,
			productCardMeasurementTitle
		} = this.getTranslations();

		switch (this.visibleModal) {
			case ProductInformationSectionModalEnum.technicalInformationCard:
				return {
					card: this.createCard('kompis-technical-information-card'),
					ariaLabel: productCardTechnicalInformationTitle
				};

			case ProductInformationSectionModalEnum.measurementsCard:
				return {
					card: this.createCard('kompis-measurements-card'),
					ariaLabel: productCardMeasurementTitle
				};

			case ProductInformationSectionModalEnum.productDetailsCard:
			default:
				return {
					card: this.createCard('kompis-product-details-card'),
					ariaLabel: productCardProductDetailsTitle
				};
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-product-information-section': ProductInformationSection;
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
	}
}
